import { computed, onBeforeUnmount, ref, watch, type Ref } from "vue";
import { haversineM, pointToPolylineM } from "./geo";
import type { LatLon, SearchHit, SearchProvider, SearchResult } from "./types";

export interface DistanceInfo { meters: number }

// Центр Алматы - только пока карта ещё не сообщила свой центр.
const FALLBACK_ORIGIN: LatLon = { lat: 43.2, lon: 76.9 };
const MIN_QUERY = 2;

export function useSearch(
  provider: SearchProvider,
  ctx: { origin: Ref<LatLon | null> },
  opts: { debounceMs?: number } = {}
) {
  const debounceMs = opts.debounceMs ?? 200;
  const query = ref("");
  const result = ref<SearchResult | null>(null);
  const loading = ref(false);
  const error = ref(false);
  const active = computed(() => query.value.trim().length >= MIN_QUERY);

  // Устаревшие ответы (медленный запрос по старому тексту) отбрасываются.
  let searchToken = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const run = async () => {
    const q = query.value.trim();
    const my = ++searchToken;
    if (q.length < MIN_QUERY) {
      result.value = null;
      loading.value = false;
      error.value = false;
      return;
    }
    loading.value = true;
    try {
      const r = await provider.search(q, { origin: ctx.origin.value ?? FALLBACK_ORIGIN });
      if (my === searchToken) {
        result.value = r;
        error.value = !!r.failed;
      }
    } catch {
      if (my === searchToken) {
        result.value = { places: [], routes: [], pois: [] };
        error.value = true;
      }
    } finally {
      if (my === searchToken) loading.value = false;
    }
  };

  watch(query, () => {
    if (timer) clearTimeout(timer);
    // Пустой запрос сбрасываем сразу, без задержки дебаунса.
    if (query.value.trim().length < MIN_QUERY) {
      void run();
      return;
    }
    timer = setTimeout(() => void run(), debounceMs);
  });
  onBeforeUnmount(() => timer && clearTimeout(timer));

  const distanceFor = (hit: SearchHit): DistanceInfo | null => {
    const from = ctx.origin.value;
    if (!from) return null;
    const meters = hit.kind === "route" ? pointToPolylineM(from, hit.line) : haversineM(from, hit);
    return { meters };
  };

  // «Повторить» после ошибки сети: тот же текст, без дебаунса; пока запрос
  // идёт - без строки ошибки и старого (неполного) результата, т.е. «Ищем…».
  const retry = () => {
    error.value = false;
    result.value = null;
    return run();
  };

  return { query, result, loading, error, active, distanceFor, retry };
}
